"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Product = void 0;

const FulfillableItemList_1 = require("./FulfillableItemList");

const LicenseQuantityList_1 = __importDefault(require("./LicenseQuantityList"));

const types_1 = require("./types");

class Product {
  /**
   * @description Creates a new Product for use.
   *
   * @param {Object} options Initialization Object (params described below)
   *
   * @param {String} options.id A Product's ID
   * @param {Boolean} options.administerable true if Product is administerable, else false.
   * @param {String} options.applicableOfferType A product's offer type: BASE, TRIAL.
   * @param {Object} options.assets The product icons. See the 'getIcon' method.
   * @param {String} options.cloud A Product's cloud; CREATIVE, DOCUMENT, or EXPERIENCE.
   * @param {String} options.code A Product's code.
   * @param {String} options.contractId The ID for the contract providing the product.
   * @param {String} options.customerSegment The customer segment for the product.
   * @param {String} options.fulfillableEntityResourceName A product's fulfillable entity resource name.
   * @param {Array} options.fulfillableItems An array of fulfillable items for the product
   * @param {Number} options.groupsQuantity A Product's total license group count
   * @param {Array} options.licenseGroups A Product's nested licenseGroups. These are only returned
   *        when the product is nested within a single user fetch (ie, /organizations/:id/users/:id)
   * @param {Array} options.licenseGroupSummaries A Product's nested licenseGroups, in a condensed data object.
   *        These can be returned by both getProducts and getProduct calls.
   * @param {Array} options.licenseQuantities A Product's license quantities
   * @param {String} options.longDescription A Product's long description
   * @param {String} options.longName A Product's long name
   * @param {String} options.offerId The Id of the offer backing this product
   * @param {String} options.orgId The Id of the org backing this product
   * @param {Object} options.pricing The pricing data associated with this product
   * @param {String} options.productArrangementCode The product arrangement code backing this product
   * @param {Number} options.provisionedQuantity A Product's provisioned quantity
   * @param {String} options.shortDescription A Product's short description
   * @param {String} options.shortName A Product's short name
   * @param {String} options.tagList A Product's tags
   */
  constructor(options) {
    if (options.fulfillableItems instanceof FulfillableItemList_1.FulfillableItemList) {
      this.fulfillableItems = options.fulfillableItems;
    } else {
      this.fulfillableItems = new FulfillableItemList_1.FulfillableItemList(options.fulfillableItems);
    }

    if (options.licenseQuantities instanceof LicenseQuantityList_1.default) {
      this.licenseQuantities = options.licenseQuantities;
    } else {
      this.licenseQuantities = new LicenseQuantityList_1.default(options.licenseQuantities);
    }

    this.administerable = options.administerable;
    this.applicableOfferType = options.applicableOfferType;
    this.assets = options.assets;
    this.buyingProgram = options.buyingProgram;
    this.cloud = options.cloud;
    this.code = options.code;
    this.contractId = options.contractId;
    this.contractIds = options.contractIds;
    this.customerSegment = options.customerSegment;
    this.customerUI = options.customerUI;
    this.delegatedQuantity = options.delegatedQuantity;
    this.delegationTargets = options.delegationTargets;
    this.family = options.family;
    this.figId = options.figId;
    this.fulfillableEntityResourceName = options.fulfillableEntityResourceName;
    this.id = options.id;
    this.licenseConfigurable = options.licenseConfigurable;
    this.groupsQuantity = options.groupsQuantity;
    this.groupParameters = options.groupParameters;
    this.licenseGroups = options.licenseGroups;
    this.licenseGroupSummaries = options.licenseGroupSummaries;
    this.longDescription = options.longDescription;
    this.longName = options.longName;
    this.offerId = options.offerId;
    this.orgId = options.orgId;
    this.pricePoint = options.pricePoint;
    this.pricing = options.pricing;
    this.productArrangementCode = options.productArrangementCode;
    this.provisionedQuantity = options.provisionedQuantity;
    this.salesChannel = options.salesChannel;
    this.shortDescription = options.shortDescription;
    this.shortName = options.shortName;
    this.tagList = options.tagList;
    this.targetExpression = options.targetExpression;
  }
  /**
   * @description Method to determine the count of licenses which may be assigned.
   *
   * @returns {Number}  quantity of licenses for this product that are assignable
   */


  getAssignableLicenseCount() {
    return this.licenseQuantities.getAssignableQuantity();
  }
  /**
   * @description Method to return the number of available (unassigned) licenses for this product
   *
   * @returns {Number} number of available licenses of Product
   */


  getAvailableLicenseCount() {
    if (this.getAssignableLicenseCount() >= this.provisionedQuantity) {
      return this.getAssignableLicenseCount() - this.provisionedQuantity;
    }

    return 0;
  }
  /**
   * @description Method to return the number of undelegated licenses for this product.
   * There is one race-condition edge case where the auto-delegated user has been delegated but not yet provisioned.
   * In this case, we should use delegated count instead of provisioned count to determine
   * how many licenses are available for assignment.
   *
   * @returns {Number} number of undelegated licenses of Product
   */


  getUndelegatedLicenseCount() {
    if (this.getAssignableLicenseCount() >= this.delegatedQuantity) {
      return this.getAssignableLicenseCount() - this.delegatedQuantity;
    }

    return 0;
  }
  /**
   * @description Method to get the icon for this product
   *
   * @returns {String} The path to the svg icon. Returns undefined if there is not an icon.
   */


  getIcon() {
    var _a, _b;

    return (_b = (_a = this.assets) === null || _a === void 0 ? void 0 : _a.icons) === null || _b === void 0 ? void 0 : _b.svg;
  }
  /**
   * @description Method to determine whether the product has no available licenses.
   *   Enterprise Direct does not require pre-purchased licenses to add more.
   *   If a license quantity is unlimited, then this will return false.
   *
   * @returns {Boolean} true if this Product has no available licenses, else false
   */


  hasNoAvailableLicenses() {
    return !this.licenseQuantities.hasUnlimitedLicenses() && this.getAvailableLicenseCount() <= 0 && !this.fulfillableItems.hasOverdelegationAllowed();
  }
  /**
   * @description Method to determine if this Product is an enterprise product.
   *
   * @returns {Boolean} true if an enterprise product, else false
   */


  isEnterprise() {
    return this.customerSegment === types_1.CUSTOMER_SEGMENT.ENTERPRISE;
  }
  /**
   * @description Method to determine if this Product is a Direct Enterprise product.
   *
   * @returns {Boolean} true if a direct enterprise product, else false
   */


  isEnterpriseDirect() {
    return this.isEnterprise() && this.salesChannel === types_1.SALES_CHANNEL.DIRECT;
  }
  /**
   * @description Method to determine if this Product is an Indirect Enterprise product (EVIP).
   *
   * @returns {Boolean} true if an indirect enterprise product, else false
   */


  isEnterpriseIndirect() {
    return this.isEnterprise() && this.salesChannel === types_1.SALES_CHANNEL.INDIRECT;
  }
  /**
   * @description Method to determine if this Product is a new one or not.
   *
   * @returns {Boolean} true if a new Product, else false
   */


  isNew() {
    return this.id === undefined || this.id === null;
  }
  /**
   * @description Method to determine if this Product is eligible for product-specific support.
   *
   * @returns {Boolean} true if product is eligible for product-specific support, else false
   */


  isProductSupportRoleAssignmentAllowed() {
    return this.fulfillableItems.hasSupportAllowedAdmins();
  }
  /**
   * @description Method to determine if this Product is a team product.
   *
   * @returns {Boolean} true if a team product, else false
   */


  isTeam() {
    return this.customerSegment === types_1.CUSTOMER_SEGMENT.TEAM;
  }
  /**
   * @description Method to transform model into the smallest representation.
   *   This helps reduce the amount of traffic our server has to deal with, in
   *   addition to altering models to conform to server/API expectations (in
   *   many cases).
   *
   * @returns {Object} minimum necessary representation of model
   */


  toMinimumModel() {
    return {
      id: this.id
    };
  }

}

exports.Product = Product;